/**
 * The note's tags, as chips you can add to and remove from.
 *
 * Shared by Read's expand-in-place panel and Cards' note dock, which carried byte-identical
 * read-only copies of this markup before it could be edited.
 *
 * **Every tag is editable here, including the app-managed ones**, and that is deliberately not the
 * Tags panel's rule. There, renaming a machine tag is refused: the enrichment rules would re-create
 * the old name collection-wide and you would carry both. Removing one from a *single* note does
 * nothing of the sort — the next run simply puts it back if it still applies — and adding `Prio` by
 * hand is the whole point of that tag. Different operation, different risk.
 */

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { useEffect, useState } from "react";

import { applyTagEdit, hasTag, normalizeTag, splitTags, suggestedTags } from "@/lib/tags";

export function TagChips({
  tags,
  onChange,
  busy = false,
}: {
  tags: string[];
  /** The full next tag list. Writes are immediate — there is no draft to save. */
  onChange: (next: string[]) => void;
  busy?: boolean;
}) {
  const [adding, setAdding] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1.5 rounded-[var(--r-4)] bg-sunken px-[9px] py-[3px] text-[14px]/[1.5] text-ink-2"
        >
          {tag}
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange(tags.filter((t) => t !== tag))}
            aria-label={`タグ ${tag} を外す`}
            className="text-[14px] text-ink-3 hover:text-accent-text disabled:opacity-40"
          >
            ×
          </button>
        </span>
      ))}

      {adding ? (
        <TagInput
          existing={tags}
          onCommit={(typed) => {
            setAdding(false);
            // **Split, don't just trim.** Anki separates tags with whitespace, so committing the
            // raw string would file `勉強　N3` as one tag containing a space — which the collection
            // cannot hold, so it silently becomes two the moment it is written and the chip you
            // were looking at was never real. `splitTags` exists for exactly that, U+3000 included.
            //
            // `applyTagEdit` rather than a local `hasTag` + append: it is the tested rule for
            // Anki's case-insensitive tag identity, and one copy of it cannot disagree with itself.
            const next = applyTagEdit(tags, { add: splitTags(typed), remove: [] });
            if (next.length !== tags.length || next.some((t, i) => t !== tags[i])) onChange(next);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => setAdding(true)}
          className="rounded-[var(--r-4)] border-[1.5px] border-dashed border-line-strong px-[9px] py-[2px] text-[14px]/[1.5] text-ink-4 hover:text-accent disabled:opacity-40"
        >
          + タグ
        </button>
      )}
    </div>
  );
}

/**
 * One tag, typed or picked.
 *
 * A real `<input>` rather than a contenteditable, which is load-bearing rather than idiomatic:
 * Cards binds bare `j`/`k` at the window to move the dock, guarded only by `isTextEntry(target)`
 * (`lib/dom.ts`). Typing a tag containing `j` into anything that guard does not recognise would
 * scroll the dock off the note mid-word.
 */
function TagInput({
  existing,
  onCommit,
  onCancel,
}: {
  existing: string[];
  onCommit: (tag: string) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState("");
  const [all, setAll] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void suggestedTags().then((names) => !cancelled && setAll(names));
    return () => {
      cancelled = true;
    };
  }, []);

  const typed = normalizeTag(query);
  const matches = all
    .filter((t) => !hasTag(existing, t))
    .filter((t) => t.toLowerCase().includes(typed.toLowerCase()))
    .slice(0, 8);

  return (
    <Combobox
      immediate
      value={null}
      onChange={(picked: string | null) => onCommit(picked ?? typed)}
    >
      <div className="relative">
        <ComboboxInput
          autoFocus
          placeholder="タグ"
          aria-label="タグを追加"
          className="w-[130px] rounded-[var(--r-4)] border-[1.5px] border-dashed border-line-strong bg-void px-[9px] py-[2px] text-[14px]/[1.5] text-ink-2 outline-none focus:border-accent"
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => onCommit(typed)}
          onKeyDown={(e) => {
            // Escape must stop here. Read's panel listens for it at the window to collapse (and
            // save) the whole card, and honours `defaultPrevented` — so without this, dismissing
            // the suggestions would close the panel you are editing.
            if (e.key === "Escape") {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
              return;
            }
            // Ctrl+Enter is the dock's save. Committing a tag should not also save the note.
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              e.stopPropagation();
              onCommit(typed);
              return;
            }
            // Plain Enter with nothing to pick has to commit what was typed, and that is the
            // common case: a tag you have never used matches no suggestion, so `ComboboxOptions`
            // is not rendered and Headless UI's own Enter handler finds no active option, closes
            // the list and returns — the tag would only ever land by clicking away. When there ARE
            // matches this is left alone, so Enter still takes the highlighted suggestion.
            //
            // **An empty box has to be intercepted too, and that is not the same case.** The
            // combobox is `immediate`, so it opens on focus, and opening sets Headless UI's
            // `defaultToFirstOption` — the first suggestion is active before a character is typed.
            // Left alone, Enter on a box you have typed nothing into selects that suggestion, and
            // `TagChips` writes immediately, so an arbitrary tag reaches the collection. Committing
            // `""` here is a no-op that just closes the input, which is what Enter should mean.
            //
            // `isComposing` is not optional in a Japanese-language tool: Enter is also how an IME
            // accepts a kana → kanji conversion, and committing on that would file the tag
            // half-typed.
            if (e.key === "Enter" && !e.nativeEvent.isComposing && (!typed || matches.length === 0)) {
              e.preventDefault();
              e.stopPropagation();
              onCommit(typed);
            }
          }}
        />
        {matches.length > 0 && (
          /**
           * `anchor`, not `absolute` — the list has to escape its scroll container.
           *
           * The tag row is the LAST thing in the dock's field column, and that column is
           * `overflow-auto` (`NoteDock.tsx`). A plain absolutely-positioned dropdown opening
           * downward is clipped by it: measured at 125px of a 154px list cut off, so one suggestion
           * of six was visible. Read's panel is the same shape.
           *
           * Headless UI's `anchor` portals the list to the document and positions it with
           * floating-ui, which also flips it above the input when there is no room below. That is
           * the whole fix — and it is invisible to a DOM-based test, because the clipped list was
           * always *present* and `getByRole('option')` passed throughout. Checked with
           * `getBoundingClientRect` against the clipping ancestor instead.
           */
          <ComboboxOptions
            anchor="bottom start"
            style={{ zIndex: "var(--z-popover)" }}
            className="max-h-[220px] w-[160px] overflow-y-auto rounded-[var(--r-2)] border border-line-strong bg-void py-1 shadow-[var(--sh-popover)] [--anchor-gap:4px]"
          >
            {matches.map((tag) => (
              <ComboboxOption
                key={tag}
                value={tag}
                className="cursor-pointer px-[10px] py-[3px] text-[14px]/[1.5] text-ink-2 data-[focus]:bg-sunken"
              >
                {tag}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </Combobox>
  );
}
